import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Params, Router } from '@angular/router';
import { AnalyticService, CommonService, NotificationService } from '@app/core';
import { AppProfileData, FilterCheck, UserListFilters, UserSelectorData, UserSelectorPanelNotes } from '@app/models';
import { OrganizationList } from '@app/models/organization.model';
import { GetOrgUserList, MemberUser, SelectorUserData } from '@app/models/user.model';
import { SelectorConfirmPanelComponent, AnalyticLogLabels, pageEvents, USER_OPERATIONS, NoProgramFilterValue, appValues } from '@app/shared';
import { of, Subject } from 'rxjs';
import { takeUntil, switchMap } from 'rxjs/operators';
import { UsersSelectorListComponent } from './users-selector-list/users-selector-list.component';
import { UsersSelectorService } from './users-selector.service';

@Component({
    selector: 'app-users-selector',
    templateUrl: './users-selector.component.html',
    styleUrls: ['./users-selector.component.scss'],
    standalone: false
})
export class UsersSelectorComponent implements OnInit, OnDestroy {

  isSelectorPanelOpened: boolean = false;
  appProfileData!: AppProfileData;
  userSelectorData!: UserSelectorData;
  usersList: GetOrgUserList | undefined = undefined;
  welcomeMessageParam = appValues.welcomeMessageParams;
  userFilterLists: UserListFilters = {
      language: [],
      gender: [
          {
              label: 'Male',
              value: 'male',
              checked: false
          },
          {
              label: 'Female',
              value: 'female',
              checked: false
          },
          {
              label: 'Non-binary',
              value: 'nonbinary',
              checked: false
          },
          {
              label: 'Unspecified',
              value: 'unspecified',
              checked: false
          }
      ],
      active: [
          {
              label: 'Active',
              value: 'true',
              checked: false
          },
          {
              label: 'Inactive',
              value: 'false',
              checked: false
          }
      ]
  };
  @ViewChild('userSelectorListCompRef') userSelectorListCompRef!: UsersSelectorListComponent;
  @ViewChild('selectorConfirmPanelCompRef') selectorConfirmPanelCompRef!: SelectorConfirmPanelComponent;
  private readonly _unsubscribeAll$: Subject<any> = new Subject();
  usersSelectorParams!: Params;
  panelNotes!: UserSelectorPanelNotes;
  reqProgress: boolean = false;
  userMessages = USER_OPERATIONS;
  noProgramFilterValue = NoProgramFilterValue;
  broadcastType: string | null = null;

  constructor(
    private readonly _commonService: CommonService, 
    private readonly _usersSelectorService: UsersSelectorService, 
    private readonly _notificationService: NotificationService, 
    private readonly _analyticService: AnalyticService, 
    private readonly _router: Router
  ) { 
    this._usersSelectorService.userSelectorData
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe(data => {
        this.userSelectorData = data;
      });
    this._usersSelectorService.panelNotes
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe(data => this.panelNotes = data);
    
    this._usersSelectorService.panelOpened
      .pipe(
        switchMap(data => {
          this.isSelectorPanelOpened = data;
          if (this.isSelectorPanelOpened) {
            return this._commonService.appProfileDataAsObservable;
          }
          else {
            this.resetAll();
            return of(null);
          }
        }), 
        takeUntil(this._unsubscribeAll$)
      )
      .subscribe(data => {
        if (data) {
          this.appProfileData = data;
          if (data?.currentOrganization?.languages?.length) {
            let filterData: FilterCheck[] = data.currentOrganization.languages.map(lang => ({ 
                label: lang,
                value: lang,
                checked: false 
            }));
            this.userFilterLists.language = [ ...filterData ];
          }
          this.setAnalyticPageEvent(
            pageEvents.selectSpecificIndividualReceipents.eventName,
            {
              'page_title': pageEvents.selectSpecificIndividualReceipents.pageTitle
            }
          );
        }
      });

      this._usersSelectorService.broadcastType
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe(data => {
        this.broadcastType = data
      });
  }

  ngOnInit(): void {
    // no initial operations required
  }

  ngOnDestroy(): void {
    this._unsubscribeAll$.next(null);
    this._unsubscribeAll$.complete();
  }

  fetchUsersList(usersSelectorParams?: Params) {
    this.reqProgress = true;
    if (usersSelectorParams) this.usersSelectorParams = usersSelectorParams;
    const filterParams = usersSelectorParams ? this._commonService.getHttpParams(usersSelectorParams) : usersSelectorParams;

    this._usersSelectorService.getOrgUserWithParams(this.organization.id, filterParams, this.broadcastType || '')
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe(
        response => {
          this.usersList = response;
          this.reqProgress = false;
        }, 
        error => {
          this._notificationService.showError(error);
          this.reqProgress = false;
        }
      );
  }

  confirmSelectedUsers(selectedUsers: MemberUser[] | SelectorUserData[] = this.selectedUsers) {
    if (this.userSelectorData) {
      this._usersSelectorService.setUserSelectorData({
        ...this.userSelectorData, 
        selectedUsers: [...selectedUsers],
        confirmedUsers: [...selectedUsers]
      });
      this._usersSelectorService.setPanelStatus(false);
      this.resetAll();
    }
  }

  openConfirmPanel() {
    this.selectorConfirmPanelCompRef?.openPanel();
    this.setAnalyticPageEvent(
      AnalyticLogLabels.IndividualUsersSelectedMembers, 
      {
        'org_id': this.appProfileData?.currentOrganization?.id
      }, 
      false
    );
    this.setAnalyticPageEvent(
      pageEvents.individualUsersSelectedMembers.eventName,
      {
        'page_title': pageEvents.individualUsersSelectedMembers.pageTitle,
        'screenName': pageEvents.individualUsersSelectedMembers.screenName
      }
    );
  }

  setUserSelectorData(selectedUsers: MemberUser[] | SelectorUserData[]) {
    if (!this.userSelectorData) return;

    this._usersSelectorService.setUserSelectorData({
      ...this.userSelectorData, 
      selectedUsers: [...selectedUsers],
      confirmedUsers: [...selectedUsers]
    });
  }

  cancelSelector() {
    this._usersSelectorService.setPanelStatus(false);
  }

  resetAll() {
    this.usersList = undefined;
    if (this.userFilterLists?.language?.length) this.userFilterLists.language = this.userFilterLists.language.map(f => ({ ...f, checked: false }));
    this.userFilterLists.gender = this.userFilterLists.gender.map(f => ({ ...f, checked: false }));
    this.userFilterLists.active = this.userFilterLists.active.map(f => ({ ...f, checked: false }));
    this.usersSelectorParams = {};
  }

  clearAllLog() {
    this.setAnalyticPageEvent(
      AnalyticLogLabels.IndividualUsersSelectedMembersClearAll, 
      {
        'org_id': this.appProfileData?.currentOrganization?.id
      }, 
      false
    );
  }

  setAnalyticPageEvent(name: string, params: {[key: string]: any}, isPageEvent: boolean = true) {
    let eventParams = { ...params };
    if (isPageEvent) {
      if (params['screenName']) {
        this._analyticService.setCurrentScreen(params['screenName']);
      }
      eventParams['page_path'] = this._router.url;
    }
    this._analyticService.logAnalyticEvent(name, { ...params });
  }

  get organization(): OrganizationList {
    return this.appProfileData.currentOrganization;
  }

  get selectedUsers(): MemberUser[] | SelectorUserData[] {
    return this.userSelectorData?.confirmedUsers || [];
  }

}
